'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/context/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TenantBranding {
  logoUrl: string | null;
  hidePlatformName: boolean;
  buttonColor: string;
}

interface BrandingContextValue {
  branding: TenantBranding;
  isLoading: boolean;
  saveBranding: (updates: Partial<TenantBranding>) => Promise<{ success: boolean; error?: string }>;
  uploadLogo: (file: File) => Promise<{ url: string | null; error?: string }>;
  removeLogo: () => Promise<void>;
}

const DEFAULT_BRANDING: TenantBranding = {
  logoUrl: null,
  hidePlatformName: false,
  buttonColor: '#7c3aed',
};

// ─── Context ──────────────────────────────────────────────────────────────────

const BrandingContext = createContext<BrandingContextValue | null>(null);

export function useBranding() {
  const ctx = useContext(BrandingContext);
  if (!ctx) throw new Error('useBranding must be used within BrandingProvider');
  return ctx;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function BrandingProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const [branding, setBranding] = useState<TenantBranding>(DEFAULT_BRANDING);
  const [isLoading, setIsLoading] = useState(false);
  const supabase = createClient();

  // ─── Fetch branding on mount / tenant change ──────────────────────────────

  const fetchBranding = useCallback(async () => {
    const tenantId = user?.activeTenantId ?? user?.tenantId;
    if (!tenantId || !user) return;

    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('tenant_branding')
        .select('logo_url, hide_platform_name, button_color')
        .eq('tenant_id', tenantId)
        .maybeSingle();

      if (!error && data) {
        setBranding({
          logoUrl: data.logo_url ?? null,
          hidePlatformName: data.hide_platform_name ?? false,
          buttonColor: data.button_color ?? '#7c3aed',
        });
      } else {
        setBranding(DEFAULT_BRANDING);
      }
    } catch {
      setBranding(DEFAULT_BRANDING);
    } finally {
      setIsLoading(false);
    }
  }, [user, supabase]);

  useEffect(() => {
    fetchBranding();
  }, [fetchBranding]);

  // ─── Save branding ────────────────────────────────────────────────────────

  const saveBranding = useCallback(async (updates: Partial<TenantBranding>) => {
    const tenantId = user?.activeTenantId ?? user?.tenantId;
    if (!tenantId) return { success: false, error: 'No tenant ID' };

    const dbUpdates: Record<string, unknown> = {};
    if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl;
    if (updates.hidePlatformName !== undefined) dbUpdates.hide_platform_name = updates.hidePlatformName;
    if (updates.buttonColor !== undefined) dbUpdates.button_color = updates.buttonColor;
    dbUpdates.updated_by = user?.id;

    try {
      // Upsert — create row if it doesn't exist yet
      const { error } = await supabase
        .from('tenant_branding')
        .upsert(
          { tenant_id: tenantId, ...dbUpdates },
          { onConflict: 'tenant_id' }
        );

      if (error) return { success: false, error: error.message };

      setBranding((prev) => ({ ...prev, ...updates }));
      return { success: true };
    } catch (err: unknown) {
      return { success: false, error: err instanceof Error ? err.message : 'Unknown error' };
    }
  }, [user, supabase]);

  // ─── Upload logo to Supabase Storage ─────────────────────────────────────

  const uploadLogo = useCallback(async (file: File) => {
    const tenantId = user?.activeTenantId ?? user?.tenantId;
    if (!tenantId) return { url: null, error: 'No tenant ID' };

    const ext = file.name.split('.').pop() ?? 'png';
    const path = `${tenantId}/logo.${ext}`;

    const { data, error } = await supabase.storage
      .from('profile-avatars')
      .upload(path, file, { upsert: true, cacheControl: '3600' });

    if (error) return { url: null, error: error.message };

    const { data: { publicUrl } } = supabase.storage
      .from('profile-avatars')
      .getPublicUrl(data.path);

    // Append cache-bust so the browser reloads the new logo
    const url = `${publicUrl}?t=${Date.now()}`;
    return { url };
  }, [user, supabase]);

  // ─── Remove logo ──────────────────────────────────────────────────────────

  const removeLogo = useCallback(async () => {
    await saveBranding({ logoUrl: null });
  }, [saveBranding]);

  return (
    <BrandingContext.Provider value={{ branding, isLoading, saveBranding, uploadLogo, removeLogo }}>
      {children}
    </BrandingContext.Provider>
  );
}
